import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Button, Chip } from "@mui/material";
import { api } from "../utils/api";
import {
  earliestActiveRateLimitUntilMs,
  parseFutureTimestamp,
  shouldDisplayXeroHealthBanner,
} from "../utils/xeroRateLimitUi";

export const XERO_HEALTH_POLL_MS = 60000;

export const XERO_HEALTH_SYSTEM_ORDER = [
  { id: "api", name: "Xero API" },
  { id: "webhook", name: "Webhook" },
  { id: "reconciliation", name: "4-hour backup check" },
  { id: "calls", name: "Active calls" },
];

const SYDNEY_FIELDS = [
  ["lastSuccessfulCallAtSydney", "Last successful call"],
  ["lastReceivedAtSydney", "Last received"],
  ["lastRunAtSydney", "Last run"],
  ["lastErrorAtSydney", "Last error"],
  ["rateLimitedUntilSydney", "Rate limit until"],
];

/** True only when session and (when provided) the active UI portal are Administrator. */
export function isAdministratorPortal(currentPortal) {
  if (sessionStorage.getItem("selectedPortal") !== "Administrator") {
    return false;
  }
  if (currentPortal === undefined) {
    return true;
  }
  return currentPortal === "Administrator";
}

export function shouldShowXeroHealth(currentPortal) {
  return isAdministratorPortal(currentPortal);
}

function resolveHealthEnabled(options) {
  if (typeof options === "boolean") {
    return options && isAdministratorPortal(undefined);
  }
  const enabled = options?.enabled !== false;
  return enabled && isAdministratorPortal(options?.currentPortal);
}

function stateChipColor(state) {
  if (state === "down") return "error";
  if (state === "degraded") return "warning";
  if (state === "ok") return "success";
  return "default";
}

export function systemsForDisplay(report) {
  const systems = report?.systems || {};
  return XERO_HEALTH_SYSTEM_ORDER.map((entry) => {
    const system = systems[entry.id] || {};
    return {
      ...entry,
      ...system,
      id: entry.id,
      name: system.name || entry.name,
      state: system.state || "unknown",
    };
  });
}

export function useXeroIntegrationHealth(options = {}) {
  const enabled = resolveHealthEnabled(options);
  const [report, setReport] = useState(null);
  const [phase, setPhase] = useState(() => (enabled ? "loading" : "idle"));

  const load = useCallback(async () => {
    if (!enabled) {
      setReport(null);
      setPhase("idle");
      return;
    }
    try {
      const response = await api("v1/xeroapi/health", "GET");
      if (response?.status === 200 && response?.data?.success && response?.data?.data) {
        setReport(response.data.data);
        setPhase("ready");
        return;
      }
      setPhase((current) => (current === "ready" ? "ready" : "error"));
    } catch {
      setPhase((current) => (current === "ready" ? "ready" : "error"));
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setReport(null);
      setPhase("idle");
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const response = await api("v1/xeroapi/health", "GET");
        if (cancelled) return;
        if (response?.status === 200 && response?.data?.success && response?.data?.data) {
          setReport(response.data.data);
          setPhase("ready");
          return;
        }
        setPhase((current) => (current === "ready" ? "ready" : "error"));
      } catch {
        if (!cancelled) {
          setPhase((current) => (current === "ready" ? "ready" : "error"));
        }
      }
    };
    setPhase((current) => (current === "ready" ? "ready" : "loading"));
    run();
    const timer = setInterval(run, XERO_HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled]);

  useRateLimitExpiryReload(report, load, enabled);

  return { report, phase, reload: load };
}

function useRateLimitExpiryReload(report, reload, enabled) {
  useEffect(() => {
    if (!enabled || !report) return undefined;
    const untilMs = earliestActiveRateLimitUntilMs(report);
    if (untilMs == null) return undefined;
    const delay = Math.max(0, untilMs - Date.now()) + 500;
    const timer = setTimeout(() => {
      reload();
    }, delay);
    return () => clearTimeout(timer);
  }, [enabled, report, reload]);
}

function rateLimitClearTimeStillActive(banner) {
  if (!banner?.clearsAt) return false;
  const until = new Date(banner.clearsAt).getTime();
  return Number.isFinite(until) && until > Date.now();
}

export function XeroHealthBanner({ banner, report }) {
  if (!shouldDisplayXeroHealthBanner(report || { banner })) return null;
  if (!banner?.show) return null;
  const tone = banner.tone === "red" ? "red" : "amber";
  const href = banner.href || "/xero";
  const showClearTime =
    banner.clearsAtSydney && rateLimitClearTimeStillActive(banner);
  return (
    <Alert
      component={RouterLink}
      to={href}
      severity={tone === "red" ? "error" : "warning"}
      data-testid="xero-health-banner"
      data-tone={tone}
      sx={{
        borderRadius: 0,
        textDecoration: "none",
        color: "inherit",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {banner.title || "Xero integration needs attention."}
      {showClearTime && !String(banner.title || "").includes(banner.clearsAtSydney) && (
        <span>{` Rate limit clears at ${banner.clearsAtSydney} (Sydney).`}</span>
      )}
    </Alert>
  );
}

export function XeroHealthBannerHost({ currentPortal } = {}) {
  const enabled = shouldShowXeroHealth(currentPortal);
  const { report } = useXeroIntegrationHealth({ enabled, currentPortal });
  if (!enabled) return null;
  return <XeroHealthBanner banner={report?.banner} report={report} />;
}

function SydneyTimes({ system }) {
  const visible = SYDNEY_FIELDS.filter(([key, label]) => {
    if (!system[key]) return false;
    if (key === "rateLimitedUntilSydney" && !system.rateLimited && !system.pausedForRateLimit) {
      return false;
    }
    return true;
  });
  if (visible.length === 0) return null;
  return (
    <ul className="mt-2 text-sm text-slate-700">
      {visible.map(([key, label]) => (
        <li key={key}>
          {label}: {system[key]} (Sydney)
        </li>
      ))}
    </ul>
  );
}

export function XeroHealthStatusPanel({ currentPortal } = {}) {
  const enabled = shouldShowXeroHealth(currentPortal);
  const { report, phase, reload } = useXeroIntegrationHealth({ enabled, currentPortal });

  if (!enabled || phase === "idle") return null;

  return (
    <div
      className="border rounded-md p-4 bg-white shadow-sm w-full max-w-3xl"
      data-testid="xero-health-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-lg font-bold">Integration health</h3>
        <Button type="button" size="small" variant="outlined" onClick={reload}>
          Refresh health
        </Button>
      </div>
      {phase === "loading" && !report && <p>Checking Xero integration health…</p>}
      {phase === "error" && !report && (
        <Alert severity="warning">Could not load Xero integration health.</Alert>
      )}
      {report && (
        <>
          <p className="text-sm text-slate-600 mb-3">
            {report.generatedAtSydney
              ? `Checked ${report.generatedAtSydney} (Australia/Sydney).`
              : "Checked just now."}{" "}
            Overall: {report.overall || "unknown"}.
          </p>
          {report.banner?.clearsAtSydney &&
            parseFutureTimestamp(report.banner?.clearsAt) && (
            <Alert severity="warning" className="mb-3" data-testid="xero-rate-limit-clears">
              {`Xero rate limit clears at ${report.banner.clearsAtSydney} (Sydney).`}
            </Alert>
          )}
          {report.billQueue && !report.billQueue.error && (
            <div
              className="border rounded p-3 mb-3 bg-slate-50"
              data-testid="xero-bill-queue-health"
            >
              <h4 className="font-semibold mb-2">Redemption Xero bill queue</h4>
              <ul className="text-sm text-slate-700 flex flex-col gap-1">
                <li>
                  Queued: {Number(report.billQueue.queuedCount) || 0} · Processing:{" "}
                  {Number(report.billQueue.processingCount) || 0} · Failed:{" "}
                  {Number(report.billQueue.failedCount) || 0}
                </li>
                {report.billQueue.oldestQueuedAgeMinutes != null &&
                  report.billQueue.oldestQueuedAtSydney && (
                    <li>
                      Oldest queued: {report.billQueue.oldestQueuedAtSydney} (Sydney) — age{" "}
                      {report.billQueue.oldestQueuedAgeMinutes} min
                    </li>
                  )}
                {report.billQueue.lastSuccessfulCronAtSydney && (
                  <li>
                    Last successful bill cron: {report.billQueue.lastSuccessfulCronAtSydney}{" "}
                    (Sydney)
                  </li>
                )}
                {report.billQueue.lastCronError && (
                  <li className="text-red-700">Last cron error: {report.billQueue.lastCronError}</li>
                )}
              </ul>
            </div>
          )}
          <ul className="flex flex-col gap-3">
            {systemsForDisplay(report).map((system) => (
              <li key={system.id} data-testid={`xero-health-system-${system.id}`} className="border rounded p-3">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="font-semibold">{system.name}</h4>
                  <Chip size="small" label={system.state} color={stateChipColor(system.state)} />
                </div>
                {system.id === "calls" && (
                  <p className="mt-2">
                    Active calls: {Number(system.inFlight) || 0} of {Number(system.limit) || 5}
                  </p>
                )}
                {system.detail && <p className="mt-2 text-sm text-slate-700">{system.detail}</p>}
                <SydneyTimes system={system} />
                {system.lastError && (
                  <p className="mt-2 text-sm text-red-700">Last error: {system.lastError}</p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
